import Vue from 'vue'
import store from 'store'
import select from 'dom-select'

/**
 * Newsletter popup trigger.
 *
 * Opens once the visitor has engaged with the page — `data-delay` seconds
 * elapsed OR `data-scroll` percent scrolled, whichever comes first — and the
 * cookie consent banner has been resolved. If the banner is still unresolved
 * after CONSENT_TIMEOUT the popup opens anyway, sitting over the banner. The
 * banner is never dismissed: closing the popup reveals it again, still waiting.
 */
const SHOWN_KEY = 'newsletter_last_shown'
const SUBSCRIBED_KEY = 'newsletter_subscribed'
const SESSION_KEY = 'newsletter'
const THROTTLE_MS = 24 * 60 * 60 * 1000
const CONSENT_TIMEOUT_MS = 20 * 1000
const BANNER_SELECTOR = '#tinycookie-wrapper, .tinycookie-box'
const BANNER_COOKIE = 'tinycookie'

const DEFAULT_DELAY_SECONDS = 6
const DEFAULT_SCROLL_PERCENT = 45

const readNumber = (node, attribute, fallback) => {
  const value = parseFloat(node && node.getAttribute(attribute))
  return value > 0 ? value : fallback
}

const wasShownRecently = () => {
  const last = Number(window.localStorage.getItem(SHOWN_KEY))
  return !!last && Date.now() - last < THROTTLE_MS
}

// TinyCookie writes several keys on resolve and their meaning is undocumented,
// so any key with the prefix counts as consent having been dealt with.
const hasConsentCookie = () => document.cookie
  .split('; ')
  .some(cookie => cookie.indexOf(BANNER_COOKIE) === 0)

const scrollProgress = () => {
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight
  return maxScroll > 0 ? window.scrollY / maxScroll : 0
}

export default el => Promise.all([
  enqueueModule('form-newsletter.vue'),
  enqueueModule('vue-image.vue')
]).then(() => new Vue({
  el,
  store,
  data () {
    return {
      showing: false,
      bannerVisible: false
    }
  },
  created () {
    this.timers = []
    this.observer = null
    this.bannerSeen = false
    this.consentResolved = hasConsentCookie()
    this.engaged = false
    this.onScroll = null

    const container = select('.newsletter-bar__container')
    this.delayMs = readNumber(el, 'data-delay', DEFAULT_DELAY_SECONDS) * 1000
    this.scrollTarget = readNumber(el, 'data-scroll', DEFAULT_SCROLL_PERCENT) / 100

    // The form posts natively and reloads the page, so the outcome of the last
    // submit is only knowable from markers rendered inside the form block.
    if (container && container.querySelector('[data-newsletter-submitted]')) {
      window.localStorage.setItem(SUBSCRIBED_KEY, '1')
      return
    }

    this.watchBanner()

    if (container && container.querySelector('[data-newsletter-error]')) {
      // Force the popup open so the visitor can see and fix the error.
      this.open({ throttle: false, force: true })
      return
    }

    if (this.isBlocked()) {
      return
    }

    this.startTriggers()
  },
  beforeDestroy () {
    this.teardownTriggers()
    this.clearTimers()
    this.disconnectObserver()
  },
  methods: {
    /**
     * Re-checked on every open attempt, not just at startup. The consent
     * observer can fire long after the visitor has already dismissed the
     * popup — without this the banner disappearing would reopen it.
     */
    isBlocked () {
      return !!window.localStorage.getItem(SUBSCRIBED_KEY) ||
        window.sessionStorage.getItem(SESSION_KEY) === 'closed' ||
        wasShownRecently()
    },

    clearTimers () {
      this.timers.forEach(window.clearTimeout)
      this.timers = []
    },

    /**
     * Both countdowns run from when the page was opened, not from when this
     * module booted. Deferred scripts can take seconds to arrive on a slow
     * connection, and without this the visitor waits that long on top of the
     * configured delay.
     */
    startTriggers () {
      const elapsed = window.performance && window.performance.now
        ? window.performance.now()
        : 0

      this.timers.push(window.setTimeout(() => {
        this.engaged = true
        this.maybeOpen()
      }, Math.max(0, this.delayMs - elapsed)))

      this.timers.push(window.setTimeout(() => {
        // Consent was never dealt with — open anyway rather than stay hidden.
        this.open()
      }, Math.max(0, CONSENT_TIMEOUT_MS - elapsed)))

      let ticking = false
      this.onScroll = () => {
        if (ticking) {
          return
        }
        ticking = true
        window.requestAnimationFrame(() => {
          ticking = false
          if (scrollProgress() >= this.scrollTarget) {
            this.engaged = true
            this.maybeOpen()
          }
        })
      }

      window.addEventListener('scroll', this.onScroll, { passive: true })
    },

    teardownTriggers () {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll)
        this.onScroll = null
      }
    },

    /**
     * The banner is injected by a third-party app embed, so its absence at load
     * means nothing — it may simply not be there yet. Only a disappearance after
     * we have actually seen it counts as the visitor having resolved it.
     */
    watchBanner () {
      if (typeof window.MutationObserver !== 'function') {
        return
      }

      this.observer = new window.MutationObserver(() => this.readBanner())
      this.observer.observe(document.body, { childList: true, subtree: true })

      this.readBanner()
    },

    disconnectObserver () {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },

    readBanner () {
      const banner = select(BANNER_SELECTOR)

      // Drives the elevated z-index: the popup only outranks the banner while
      // the banner is actually on screen, so it never covers the cart drawer.
      this.bannerVisible = !!banner

      if (banner) {
        this.bannerSeen = true
      } else if (this.bannerSeen) {
        this.consentResolved = true
      }

      if (hasConsentCookie()) {
        this.consentResolved = true
      }

      // Nothing left to watch once consent is settled and the banner is gone.
      if (this.consentResolved && !banner) {
        this.disconnectObserver()
      }

      this.maybeOpen()
    },

    maybeOpen () {
      if (this.showing || !this.engaged || !this.consentResolved) {
        return
      }
      this.open()
    },

    open ({ throttle = true, force = false } = {}) {
      if (this.showing || (!force && this.isBlocked())) {
        return
      }

      this.showing = true
      this.teardownTriggers()
      this.clearTimers()

      if (throttle) {
        window.localStorage.setItem(SHOWN_KEY, String(Date.now()))
      }

      document.documentElement.classList.add('newsletter-bar-active')
    },

    close () {
      window.sessionStorage.setItem(SESSION_KEY, 'closed')
      this.showing = false
      document.documentElement.classList.remove('newsletter-bar-active')
    }
  }
})
)
